import React, { useState, useEffect, useCallback } from 'react';
import { Input } from './ui/input';

/**
 * NumberInput — decimal input that:
 *   - accepts comma (,) or dot (.) as decimal separator (Swiss/IT locale)
 *   - allows the user to fully clear the field (empty string) instead of forcing 0
 *   - emits `null` when empty, or a `number` when parseable
 *
 * Props:
 *   - value: number | null | undefined
 *   - onChange: (val: number | null) => void
 *   - onBlur: (val: number | null) => void
 *   - min, max, step, className, placeholder, disabled, 'data-testid'
 *   - allowNegative (default false)
 */
export const NumberInput = ({
  value,
  onChange,
  onBlur,
  min,
  max,
  step,
  className,
  placeholder,
  disabled,
  allowNegative = false,
  ...rest
}) => {
  const format = useCallback((v) => {
    if (v === null || v === undefined || v === '') return '';
    return String(v);
  }, []);

  const [raw, setRaw] = useState(format(value));

  // Sync when parent value changes AND local input isn't currently being edited
  // (skip if current raw already represents same numeric value)
  useEffect(() => {
    const currentNum = raw === '' ? null : parseFloat(raw.replace(',', '.'));
    if (currentNum === value) return;
    if (Number.isNaN(currentNum) && (value === null || value === undefined)) return;
    setRaw(format(value));
  }, [value]); // eslint-disable-line

  const emit = (str) => {
    if (str === '' || str === '-' || str === ',' || str === '.') {
      onChange && onChange(null);
      return;
    }
    const normalized = str.replace(',', '.');
    const n = parseFloat(normalized);
    if (!Number.isNaN(n)) onChange && onChange(n);
  };

  const handleChange = (e) => {
    const v = e.target.value;
    // Allow: empty, digits, optional single decimal separator (, or .), optional leading '-'
    const pattern = allowNegative
      ? /^-?[0-9]*[.,]?[0-9]*$/
      : /^[0-9]*[.,]?[0-9]*$/;
    if (!pattern.test(v)) return;
    setRaw(v);
    emit(v);
  };

  const handleBlur = () => {
    if (raw === '' || raw === '-' || raw === ',' || raw === '.') {
      onBlur && onBlur(null);
      return;
    }
    const n = parseFloat(raw.replace(',', '.'));
    if (Number.isNaN(n)) {
      onBlur && onBlur(null);
    } else {
      // normalize display (e.g. "1," -> "1")
      setRaw(String(n));
      onBlur && onBlur(n);
    }
  };

  return (
    <Input
      type="text"
      inputMode="decimal"
      autoComplete="off"
      value={raw}
      onChange={handleChange}
      onBlur={handleBlur}
      placeholder={placeholder}
      disabled={disabled}
      className={className}
      min={min}
      max={max}
      step={step}
      {...rest}
    />
  );
};

export default NumberInput;
